// src/services/shift.ts

import apiClient from "../../client/apiClient";
import { appendQueryParams } from "../../utils";

// ==================== TYPES (matching your backend Shift model) ====================

export type DayConfig = {
  day:
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";
  checkIn: string;       // e.g. "09:00 AM"
  checkOut: string;      // e.g. "05:00 PM"
  lateAfter: string;     // e.g. "09:15 AM"
  minimumHours: number;  // e.g. 8
  halfDayAfter: string;  // e.g. "01:00 PM"
};

export type Shift = {
  _id: string;
  title: string;
  applicableTo: "student" | "employee" | "both";
  system: {
    _id: string;
    name?: string;
  };
  branch: {
    _id: string;
    name?: string;
  };
  dayWiseConfig: DayConfig[];
  // Which classes this shift applies to. Empty/omitted = the classless
  // default shift for everyone (findStudentShift's fallback) -- only
  // meaningful for applicableTo "student"/"both".
  classes?: { _id: string; name?: string }[];
  isDeleted?: boolean;
  createdAt: string;
  updatedAt: string;
  deletedBy?: {
    _id: string;
    name?: string;
    email?: string;
  };
};

export type CreateShiftData = {
  title: string;
  applicableTo: "student" | "employee" | "both";
  system: string;
  branch: string;
  dayWiseConfig: DayConfig[];
  classes?: string[];
};

export type UpdateShiftData = Partial<CreateShiftData>;

export type PaginationData = {
  currentPage: number;
  totalPages: number;
  total: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
};

export type ShiftResponse = {
  success: boolean;
  message: string;
  data: Shift;
};

export type ShiftsListResponse = {
  success: boolean;
  message: string;
  data: Shift[];
  count?: number;
};

export type ShiftsWithPaginationResponse = {
  success: boolean;
  message: string;
  data: {
    shifts: Shift[];
    pagination: PaginationData;
  };
};

// ==================== ENDPOINTS (matching your backend controller) ====================

const SHIFT_ENDPOINT = {
  BASE: "/shift",                              // GET (list), POST (create)
  BY_ID: (id: string) => `/shift/${id}`,       // GET, PUT, DELETE
  CLASS: (classId: string) => `/shift/class/${classId}`,
  EMPLOYEE: (employeeId: string) => `/shift/employee/${employeeId}`,
};

// ==================== SERVICE FUNCTIONS ====================

/**
 * Create a new shift
 */
const createShift = async (data: CreateShiftData): Promise<ShiftResponse> => {
  const response = await apiClient({
    url: SHIFT_ENDPOINT.BASE,
    method: "POST",
    data,
  });
  return response.data;
};

/**
 * Get paginated list of shifts
 */
const getAllShifts = async ({
  systemId,
  branchId,
  applicableTo,
  search = "",
  sortBy = "createdAt",
  sortOrder = "desc",
  page = 1,
  limit = 10,
}: {
  systemId?: string;
  branchId?: string;
  applicableTo?: "student" | "employee" | "both";
  search?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  page?: number;
  limit?: number;
} = {}): Promise<ShiftsWithPaginationResponse> => {
  const response = await apiClient({
    url: appendQueryParams(SHIFT_ENDPOINT.BASE, {
      systemId,
      branchId,
      applicableTo,
      search,
      sortBy,
      sortOrder,
      page,
      limit,
    }),
    method: "GET",
  });
  return response.data;
};

/**
 * Get all shifts without pagination (useful for dropdowns)
 */
const getAllShiftsList = async ({
  systemId,
  branchId,
  applicableTo,
}: {
  systemId?: string;
  branchId?: string;
  applicableTo?: "student" | "employee" | "both";
} = {}): Promise<ShiftsListResponse> => {
  const response = await apiClient({
    url: appendQueryParams(SHIFT_ENDPOINT.BASE, {
      systemId,
      branchId,
      applicableTo,
      limit: 1000, // large limit to get all
    }),
    method: "GET",
  });
  return response.data;
};

/**
 * Get single shift by ID
 */
const getShiftById = async (id: string): Promise<ShiftResponse> => {
  const response = await apiClient({
    url: SHIFT_ENDPOINT.BY_ID(id),
    method: "GET",
  });
  return response.data;
};

/**
 * Update shift
 */
const updateShift = async (
  id: string,
  data: UpdateShiftData
): Promise<ShiftResponse> => {
  const response = await apiClient({
    url: SHIFT_ENDPOINT.BY_ID(id),
    method: "PUT", // or "PATCH" – change if backend uses PATCH
    data,
  });
  return response.data;
};

/**
 * Delete shift (soft delete)
 */
const deleteShift = async (id: string): Promise<ShiftResponse> => {
  const response = await apiClient({
    url: SHIFT_ENDPOINT.BY_ID(id),
    method: "DELETE",
  });
  return response.data;
};

/**
 * Get shifts applicable to a class (student or both)
 */
const getShiftsByClass = async (
  classId: string,
  systemId: string,
  branchId: string
): Promise<ShiftsListResponse> => {
  const response = await apiClient({
    url: appendQueryParams(SHIFT_ENDPOINT.CLASS(classId), {
      systemId,
      branchId,
    }),
    method: "GET",
  });
  return response.data;
};

/**
 * Get shifts applicable to an employee
 */
const getShiftsByEmployee = async (
  employeeId: string,
  systemId: string,
  branchId: string
): Promise<ShiftsListResponse> => {
  const response = await apiClient({
    url: appendQueryParams(SHIFT_ENDPOINT.EMPLOYEE(employeeId), {
      systemId,
      branchId,
    }),
    method: "GET",
  });
  return response.data;
};

export {
  createShift,
  getAllShifts,
  getAllShiftsList,
  getShiftById,
  updateShift,
  deleteShift,
  getShiftsByClass,
  getShiftsByEmployee,
};